// Pure view layer for the Pass Map module — ported from js/renderers-shotmap.js (the Pass
// Map section: drawPasses' line data + tooltip, wireMagneticPassHover's hit rule and its
// cross-widget passive state, passDetailsPanelHtml, passStatsBarHtml, renderPassTable).
// DOM-free and unit-tested; PassMapModule paints what these return as JSX.

import type { ProtoEvent } from "@catbento/shared";
import {
  formatMatchDate,
  nextMagneticSegment,
  normalize,
  oppositionLabel,
  passBodyPart,
  passCompleted,
  passOf,
  passTypeName,
  recipientShortName,
  sbAttrs,
  type EventContext,
  type MagneticRadii,
} from "../engines/event-map-kit.js";
import { eventMatchRows, eventTimeLabel } from "./event-details-view.js";
import { clockMinute } from "../data/match-minute.js";

// --- Lines ---------------------------------------------------------------------------------

export interface PassLine {
  pass: ProtoEvent;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  completed: boolean;
  tooltip: string;
}

/** "Opponent (H)\nTo Russo · 23'\nComplete · Open Play · Right Foot" — verbatim. */
export function passTooltip(ctx: EventContext, pass: ProtoEvent): string {
  const foot = passBodyPart(pass);
  const completed = passCompleted(pass);
  return `${oppositionLabel(ctx, pass)}\nTo ${recipientShortName(ctx, pass)} · ${clockMinute(pass.minute, pass.period)}\n${completed ? "Complete" : "Incomplete"} · ${passTypeName(pass)}${foot ? " · " + foot : ""}`;
}

export function passLine(ctx: EventContext, pass: ProtoEvent): PassLine {
  const [x1, y1] = normalize(pass.x, pass.y);
  const [x2, y2] = normalize(pass.x_end, pass.y_end);
  return { pass, x1, y1, x2, y2, completed: passCompleted(pass), tooltip: passTooltip(ctx, pass) };
}

// --- Magnetic hover --------------------------------------------------------------------------
// A pass line is 0.25 units wide and 1,000+ of them overlap, so native hit-testing is
// useless — the kit's nextMagneticSegment with Pass Map's radii (pitch units).

export const PASS_SELECT_RADIUS = 1.5;
export const PASS_STICKY_RADIUS = 3;
export const PASS_SWITCH_MARGIN = 0.6;
const PASS_RADII: MagneticRadii = { select: PASS_SELECT_RADIUS, sticky: PASS_STICKY_RADIUS, switchMargin: PASS_SWITCH_MARGIN };

type Segment = { x1: number; y1: number; x2: number; y2: number };

export function nextHoveredPass<T extends Segment>(p: { x: number; y: number }, lines: T[], current: T | null): T | null {
  return nextMagneticSegment(p, lines, current, PASS_RADII);
}

// --- Cross-widget passive state -------------------------------------------------------------
// With no local hover, what the pitch shows — in priority order: another widget's hovered
// pass (the echo, reverts when that cursor leaves), else a group selection (Passing
// Profile's clicked wedge, persists until cleared), else nothing. Kept apart so clearing one
// never wipes the other (the legacy's applyPassiveState bug fix).

export type PassPassiveState =
  | { kind: "echo"; matchId: string | null }
  | { kind: "group"; ids: ReadonlySet<string> }
  | { kind: "none" };

export function resolvePassiveState(
  echoedId: string | null,
  groupIds: ReadonlySet<string> | null,
  hasLine: (id: string) => boolean,
): PassPassiveState {
  if (echoedId != null) return { kind: "echo", matchId: hasLine(echoedId) ? echoedId : null };
  if (groupIds) return { kind: "group", ids: groupIds };
  return { kind: "none" };
}

/** A line's classes under the passive state. A group member keeps its normal width (a
 *  whole cluster thickened reads as messy); only the rest dim. */
export function passiveLineFlags(id: string, state: PassPassiveState): { selected: boolean; dimmed: boolean } {
  if (state.kind === "echo") {
    return { selected: id === state.matchId, dimmed: state.matchId != null && id !== state.matchId };
  }
  if (state.kind === "group") return { selected: false, dimmed: !state.ids.has(id) };
  return { selected: false, dimmed: false };
}

/** A local hover: the hovered line selected, every other dimmed. */
export function hoverLineFlags(id: string, hoveredId: string): { selected: boolean; dimmed: boolean } {
  return { selected: id === hoveredId, dimmed: id !== hoveredId };
}

// --- Pass Details panel ------------------------------------------------------------------------

/** StatsBomb angle (radians, 0 = attacking-forward). Only 3 buckets — which screen side is
 *  "left" for the attacking team can't be confirmed from this data, so it isn't guessed. */
export function passDirectionLabel(angleRad: number): string {
  const deg = Math.abs((angleRad * 180) / Math.PI);
  if (deg <= 45) return "Forward";
  if (deg >= 135) return "Backward";
  return "Sideways";
}

const PASS_CHARACTERISTIC_FLAGS: readonly (readonly [string, string])[] = [
  ["cross", "Cross"], ["switch", "Switch"], ["through_ball", "Through Ball"],
  ["cut_back", "Cut Back"], ["backheel", "Backheel"], ["aerial_won", "Aerial Won"],
  ["deflected", "Deflected"], ["inswinging", "Inswinging"], ["outswinging", "Outswinging"],
  ["straight", "Straight"], ["no_touch", "No Touch"],
];

export function passCharacteristics(info: Record<string, unknown>): string[] {
  return PASS_CHARACTERISTIC_FLAGS.filter(([key]) => info[key]).map(([, label]) => label);
}

export interface PassDetails {
  opposition: string;
  date: string;
  resultText: string;
  resultClass: string;
  time: string;
  outcome: string;
  outcomePillClass: string;
  bodyPart: string;
  type: string;
  height: string;
  recipient: string;
  keyPass: string;
  direction: string;
  playPattern: string;
  successProbability: string;
  characteristics: string[];
  passInto: string[];
}

export function passDetails(ctx: EventContext, pass: ProtoEvent): PassDetails {
  const raw = sbAttrs(pass);
  const info = passOf(pass);
  const enriched = (pass.enriched_event_attributes || {}) as { pass_into?: { danger?: boolean; pressure?: boolean } };
  const completed = passCompleted(pass);
  const passInto = enriched.pass_into;
  return {
    ...eventMatchRows(ctx, pass),
    time: eventTimeLabel(pass),
    outcome: completed ? "Complete" : info.outcome ? info.outcome.name : "Incomplete",
    outcomePillClass: completed ? "event-pill-good" : "event-pill-bad",
    bodyPart: passBodyPart(pass) || "—",
    type: passTypeName(pass),
    height: info.height ? info.height.name : "—",
    recipient: recipientShortName(ctx, pass),
    keyPass: info.goal_assist ? "Goal Assist" : info.shot_assist ? "Key Pass" : "—",
    direction: typeof info.angle === "number" ? passDirectionLabel(info.angle) : "—",
    playPattern: raw.play_pattern ? raw.play_pattern.name : "—",
    successProbability:
      typeof info.pass_success_probability === "number" ? `${(info.pass_success_probability * 100).toFixed(1)}%` : "—",
    characteristics: passCharacteristics(info),
    passInto: [passInto && passInto.danger ? "Danger" : "", passInto && passInto.pressure ? "Pressure" : ""].filter(Boolean),
  };
}

// --- Stats bar + table -------------------------------------------------------------------------

/** "N of TOTAL" once narrowed, and the success rate. */
export function passStats(passes: ProtoEvent[], allPasses: ProtoEvent[]): { shown: string; success: string } {
  const complete = passes.filter(passCompleted).length;
  const pct = passes.length ? (complete / passes.length) * 100 : 0;
  return {
    shown:
      allPasses.length !== passes.length
        ? `${passes.length.toLocaleString()} of ${allPasses.length.toLocaleString()}`
        : `${passes.length.toLocaleString()}`,
    success: `${pct.toFixed(1)}%`,
  };
}

export interface PassTableRow {
  key: string;
  /** Opponent (H/A), or the date where matchResults has no fixture. */
  opposition: string;
  minute: number;
  /** As football shows it ("87'", "90+3'"). */
  clock: string;
  type: string;
  bodyPart: string;
  result: string;
}

/** Newest first — the legacy comparator verbatim (ties land exactly where they did). */
export function passTableRows(ctx: EventContext, passes: ProtoEvent[]): PassTableRow[] {
  return passes
    .slice()
    .sort((a, b) => (a.match_date < b.match_date ? 1 : -1))
    .map((p) => ({
      key: p.id,
      opposition: oppositionLabel(ctx, p) || formatMatchDate(p.match_date),
      minute: p.minute,
      clock: clockMinute(p.minute, p.period),
      type: passTypeName(p),
      bodyPart: passBodyPart(p) || "—",
      result: passCompleted(p) ? "Complete" : "Incomplete",
    }));
}

// --- Summary (live, Ben 2026-10-20: Catalyst's Pass Map "Summary" tile) --------------------
// The same rules as Catalyst's engine (soccer-aggregates v1.7.0 classifierHelpers +
// aggregateDefinitions), so the numbers match theirs: an offside pass (outcome 76) isn't a pass;
// a pass with no outcome is successful; xPass = pass_success_probability (missing → 0 in the sums,
// and in no difficulty band); hard < 0.5 ≤ medium ≤ 0.8 < easy; PSAX = Σ (success − xPass); a key
// pass is a goal or shot assist; a set piece is a corner / free kick / throw-in / goal kick / kick-off
// (pass types 61, 62, 63, 65, 67).

const OFFSIDE_OUTCOME = 76;
const SET_PIECE_PASS_TYPES: ReadonlySet<number> = new Set([61, 62, 63, 65, 67]);

/** The Summary rows that pick out passes (click one → only those highlighted, Ben 2026-10-20). */
export type PassSummaryKey = "total" | "successful" | "easy" | "medium" | "hard" | "key" | "setPiece";

/** Is a pass in a Summary row? The same rules as the counts below. Pure. */
export function inSummaryRow(key: PassSummaryKey, e: ProtoEvent): boolean {
  const p = passOf(e) ?? {};
  if (p.outcome?.id === OFFSIDE_OUTCOME) return false;
  const xpass = typeof p.pass_success_probability === "number" ? p.pass_success_probability : null;
  switch (key) {
    case "total":
      return true;
    case "successful":
      return !p.outcome;
    case "easy":
      return xpass !== null && xpass > 0.8;
    case "medium":
      return xpass !== null && xpass >= 0.5 && xpass <= 0.8;
    case "hard":
      return xpass !== null && xpass < 0.5;
    case "key":
      return p.goal_assist === true || p.shot_assist === true;
    case "setPiece":
      return p.type?.id !== undefined && SET_PIECE_PASS_TYPES.has(p.type.id);
  }
}

/** A Summary row's words, for the shared selection's chip ("Key passes"). */
export const PASS_SUMMARY_LABEL: Record<PassSummaryKey, string> = {
  total: "All passes",
  successful: "Successful passes",
  easy: "Easy passes",
  medium: "Medium passes",
  hard: "Hard passes",
  key: "Key passes",
  setPiece: "Set-piece passes",
};

export interface PassSummaryRow {
  label: string;
  /** A row that picks out passes ("total" = every pass again); none for Expected / PSAX (sums). */
  key?: PassSummaryKey;
  value: string;
  /** Share of the total, or "" (Total, Key Passes, Set Piece). */
  share: string;
  /** Starts a new group (a heavier line above, as Catalyst's). */
  groupStart?: boolean;
}

export function passSummary(events: readonly ProtoEvent[]): PassSummaryRow[] {
  const passes = events.filter((e) => passOf(e)?.outcome?.id !== OFFSIDE_OUTCOME);
  const total = passes.length;
  let successful = 0;
  let expected = 0;
  let easy = 0;
  let medium = 0;
  let hard = 0;
  let key = 0;
  let setPiece = 0;
  for (const e of passes) {
    const p = passOf(e) ?? {};
    const xpass = typeof p.pass_success_probability === "number" ? p.pass_success_probability : null;
    if (!p.outcome) successful++;
    if (xpass !== null) {
      expected += xpass;
      if (xpass < 0.5) hard++;
      else if (xpass <= 0.8) medium++;
      else easy++;
    }
    if (p.goal_assist === true || p.shot_assist === true) key++;
    if (p.type?.id !== undefined && SET_PIECE_PASS_TYPES.has(p.type.id)) setPiece++;
  }
  const pct = (n: number) => (total ? `${((n / total) * 100).toFixed(1)}%` : "—");
  return [
    { label: "Total", key: "total", value: total.toLocaleString(), share: "" },
    { label: "Successful", key: "successful", value: successful.toLocaleString(), share: pct(successful) },
    { label: "Expected Successful Passes", value: expected.toFixed(2), share: pct(expected) },
    { label: "PSAX", value: (successful - expected).toFixed(2), share: pct(successful - expected) },
    { label: "Easy Passes", key: "easy", value: easy.toLocaleString(), share: pct(easy) },
    { label: "Medium Passes", key: "medium", value: medium.toLocaleString(), share: pct(medium) },
    { label: "Hard Passes", key: "hard", value: hard.toLocaleString(), share: pct(hard) },
    { label: "Key Passes", key: "key", value: key.toLocaleString(), share: "", groupStart: true },
    { label: "Set Piece", key: "setPiece", value: setPiece.toLocaleString(), share: "" },
  ];
}
